/**
 * Behavior Analysis Components
 * Visualizes user behavior data from PostHog
 */

import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  FunnelChart,
  Funnel,
  LabelList,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
} from 'recharts';
import clsx from 'clsx';
import type {
  EventCount,
  FeatureUsageStats,
  ScreenViewStats,
  UserJourneyStep,
} from '@/lib/api/analytics';
import { useI18n } from '@/i18n';

// ============================================================
// Event Stats Components
// ============================================================

interface TopEventsChartProps {
  events: EventCount[];
  className?: string;
}

export function TopEventsChart({ events, className }: TopEventsChartProps) {
  const { t } = useI18n();
  const formatEventName = useFormatEventName();
  const data = useMemo(() => {
    return events.slice(0, 10).map((e) => ({
      name: formatEventName(e.event),
      count: e.count,
      users: e.unique_users,
    }));
  }, [events, formatEventName]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">{t('reportsPage.behavior.topEvents')}</h4>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" stroke="#888" fontSize={12} />
            <YAxis
              type="category"
              dataKey="name"
              stroke="#888"
              fontSize={11}
              width={120}
              tick={{ fill: '#374151' }}
            />
            <Tooltip
              formatter={(value, name) => {
                const numValue = typeof value === 'number' ? value : 0;
                const label = name === 'count' ? t('reportsPage.behavior.triggerCount') : t('reportsPage.behavior.uniqueUsers');
                return [numValue.toLocaleString(), label];
              }}
            />
            <Legend />
            <Bar dataKey="count" name={t('reportsPage.behavior.triggerCount')} fill="#3b82f6" />
            <Bar dataKey="users" name={t('reportsPage.behavior.uniqueUsers')} fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface DailyEventsChartProps {
  dailyStats: { date: string; total_events: number; unique_users: number }[];
  className?: string;
}

export function DailyEventsChart({ dailyStats, className }: DailyEventsChartProps) {
  const { t } = useI18n();
  const data = useMemo(() => {
    return [...dailyStats]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({
        date: d.date.slice(5), // MM-DD
        events: d.total_events,
        users: d.unique_users,
      }));
  }, [dailyStats]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">{t('reportsPage.behavior.dailyTrend')}</h4>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" stroke="#888" fontSize={12} />
            <YAxis stroke="#888" fontSize={12} />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="events"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6', r: 3 }}
              name={t('reportsPage.behavior.eventCount')}
            />
            <Line
              type="monotone"
              dataKey="users"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: '#10b981', r: 3 }}
              name={t('reportsPage.behavior.activeUsers')}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================
// Feature Usage Components
// ============================================================

interface FeatureUsageChartProps {
  features: FeatureUsageStats[];
  className?: string;
}

export function FeatureUsageChart({ features, className }: FeatureUsageChartProps) {
  const { t } = useI18n();
  const formatFeatureName = useFormatFeatureName();
  const data = useMemo(() => {
    return features.slice(0, 10).map((f) => ({
      name: formatFeatureName(f.feature_name),
      clicks: f.click_count,
      users: f.unique_users,
    }));
  }, [features, formatFeatureName]);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">{t('reportsPage.behavior.featureDistribution')}</h4>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              dataKey="clicks"
              label={({ name, percent }) =>
                `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
              }
              labelLine={false}
            >
              {data.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => {
                const numValue = typeof value === 'number' ? value : 0;
                return [numValue.toLocaleString(), t('reportsPage.behavior.clickCount')];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface FeatureUsageTableProps {
  features: FeatureUsageStats[];
  className?: string;
}

export function FeatureUsageTable({ features, className }: FeatureUsageTableProps) {
  const { t } = useI18n();
  const formatFeatureName = useFormatFeatureName();
  return (
    <div className={clsx('overflow-x-auto', className)}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">{t('reportsPage.behavior.featureDetails')}</h4>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              {t('reportsPage.behavior.featureName')}
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              {t('reportsPage.behavior.clickCount')}
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              {t('reportsPage.behavior.uniqueUsers')}
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              {t('reportsPage.behavior.avgDuration')}
            </th>
          </tr>
        </thead>
        <tbody>
          {features.map((feature) => (
            <tr
              key={feature.feature_name}
              className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <td className="px-4 py-3 text-sm text-gray-900">
                {formatFeatureName(feature.feature_name)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-900">
                {feature.click_count.toLocaleString()}
              </td>
              <td className="px-4 py-3 text-sm text-gray-900">
                {feature.unique_users.toLocaleString()}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {feature.avg_duration_ms
                  ? `${(feature.avg_duration_ms / 1000).toFixed(1)}s`
                  : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Screen Views Components
// ============================================================

interface ScreenViewsChartProps {
  screens: ScreenViewStats[];
  className?: string;
}

export function ScreenViewsChart({ screens, className }: ScreenViewsChartProps) {
  const { t } = useI18n();
  const data = useMemo(() => {
    return screens.slice(0, 10).map((s) => ({
      name: formatScreenName(s.screen_name),
      views: s.view_count,
      users: s.unique_users,
    }));
  }, [screens]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">{t('reportsPage.behavior.pageViews')}</h4>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" stroke="#888" fontSize={12} />
            <YAxis
              type="category"
              dataKey="name"
              stroke="#888"
              fontSize={11}
              width={100}
              tick={{ fill: '#374151' }}
            />
            <Tooltip
              formatter={(value, name) => {
                const numValue = typeof value === 'number' ? value : 0;
                const label = name === 'views' ? t('reportsPage.behavior.viewCount') : t('reportsPage.behavior.uniqueUsers');
                return [numValue.toLocaleString(), label];
              }}
            />
            <Legend />
            <Bar dataKey="views" name={t('reportsPage.behavior.viewCount')} fill="#8b5cf6" />
            <Bar dataKey="users" name={t('reportsPage.behavior.uniqueUsers')} fill="#f59e0b" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================
// User Journey Components
// ============================================================

interface UserJourneyFunnelProps {
  steps: UserJourneyStep[];
  className?: string;
}

export function UserJourneyFunnel({ steps, className }: UserJourneyFunnelProps) {
  const { t } = useI18n();
  const data = useMemo(() => {
    return steps.map((step, index) => ({
      name: step.step_name,
      value: step.users,
      fill: getFunnelColor(index),
      conversion: step.conversion_rate,
    }));
  }, [steps]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">{t('reportsPage.behavior.userJourneyFunnel')}</h4>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <FunnelChart>
            <Tooltip
              formatter={(value, _name, props) => {
                const numValue = typeof value === 'number' ? value : 0;
                const conversion = props.payload?.conversion ?? 0;
                return [
                  `${numValue.toLocaleString()} ${t('reportsPage.behavior.userCount')} (${conversion}%)`,
                  props.payload?.name,
                ];
              }}
            />
            <Funnel dataKey="value" data={data} isAnimationActive>
              <LabelList
                position="right"
                fill="#374151"
                stroke="none"
                dataKey="name"
              />
              <LabelList
                position="center"
                fill="#fff"
                stroke="none"
                formatter={(value) => {
                  const numValue = typeof value === 'number' ? value : 0;
                  return numValue.toLocaleString();
                }}
              />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      </div>
      {/* Conversion table */}
      <div className="mt-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="px-3 py-2 text-left text-gray-500">{t('reportsPage.behavior.step')}</th>
              <th className="px-3 py-2 text-left text-gray-500">{t('reportsPage.behavior.userCount')}</th>
              <th className="px-3 py-2 text-left text-gray-500">{t('reportsPage.behavior.conversionRate')}</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step) => (
              <tr key={step.step_name} className="border-b border-gray-100">
                <td className="px-3 py-2 text-gray-900">{step.step_name}</td>
                <td className="px-3 py-2 text-gray-900">
                  {step.users.toLocaleString()}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={clsx(
                      'font-medium',
                      step.conversion_rate >= 50
                        ? 'text-green-600'
                        : step.conversion_rate >= 20
                        ? 'text-amber-600'
                        : 'text-red-600'
                    )}
                  >
                    {step.conversion_rate}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// Retention Components
// ============================================================

interface RetentionCardsProps {
  retention: Record<string, number>;
  className?: string;
}

export function RetentionCards({ retention, className }: RetentionCardsProps) {
  const { t } = useI18n();
  const retentionData = [
    { label: t('reportsPage.behavior.retention.d1'), key: 'D1', description: t('reportsPage.behavior.retention.d1Desc') },
    { label: t('reportsPage.behavior.retention.d7'), key: 'D7', description: t('reportsPage.behavior.retention.d7Desc') },
    { label: t('reportsPage.behavior.retention.d30'), key: 'D30', description: t('reportsPage.behavior.retention.d30Desc') },
  ];

  return (
    <div className={clsx('grid grid-cols-3 gap-4', className)}>
      {retentionData.map(({ label, key, description }) => {
        const value = retention[key] ?? 0;
        return (
          <div
            key={key}
            className="bg-white rounded-lg p-4 border border-gray-200"
          >
            <div className="text-sm text-gray-500">{label}</div>
            <div
              className={clsx(
                'text-2xl font-bold mt-1',
                value >= 30
                  ? 'text-green-600'
                  : value >= 15
                  ? 'text-amber-600'
                  : 'text-red-600'
              )}
            >
              {value.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-400 mt-1">{description}</div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// Summary Stats Components
// ============================================================

interface BehaviorSummaryCardsProps {
  summary: {
    total_events: number;
    unique_users: number;
    avg_events_per_user: number;
  };
  className?: string;
}

export function BehaviorSummaryCards({
  summary,
  className,
}: BehaviorSummaryCardsProps) {
  const { t } = useI18n();
  const cards = [
    {
      label: t('reportsPage.behavior.summary.totalEvents'),
      value: summary.total_events.toLocaleString(),
      subtitle: t('reportsPage.behavior.summary.behaviorEvents'),
    },
    {
      label: t('reportsPage.behavior.summary.activeUsers'),
      value: summary.unique_users.toLocaleString(),
      subtitle: t('reportsPage.behavior.uniqueUsers'),
    },
    {
      label: t('reportsPage.behavior.summary.eventsPerUser'),
      value: summary.avg_events_per_user.toFixed(1),
      subtitle: t('reportsPage.behavior.summary.avgPerUser'),
    },
  ];

  return (
    <div className={clsx('grid grid-cols-3 gap-4', className)}>
      {cards.map(({ label, value, subtitle }) => (
        <div
          key={label}
          className="bg-white rounded-lg p-4 border border-gray-200"
        >
          <div className="text-sm text-gray-500">{label}</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
          <div className="text-xs text-gray-400 mt-1">{subtitle}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

function useFormatEventName() {
  const { t } = useI18n();
  return (event: string): string => {
    const mapping: Record<string, string> = {
      app_opened: t('reportsPage.behavior.eventNames.appOpened'),
      chat_started: t('reportsPage.behavior.eventNames.chatStarted'),
      chat_message_sent: t('reportsPage.behavior.eventNames.chatMessageSent'),
      feature_clicked: t('reportsPage.behavior.eventNames.featureClicked'),
      screen_viewed: t('reportsPage.behavior.eventNames.screenViewed'),
      subscription_viewed: t('reportsPage.behavior.eventNames.subscriptionViewed'),
      soulmate_started: t('reportsPage.behavior.eventNames.soulmateStarted'),
      '$pageview': t('reportsPage.behavior.eventNames.pageview'),
      '$autocapture': t('reportsPage.behavior.eventNames.autocapture'),
    };
    return mapping[event] || event.replace(/_/g, ' ');
  };
}

function useFormatFeatureName() {
  const { t } = useI18n();
  return (name: string): string => {
    const mapping: Record<string, string> = {
      soulmate: t('reportsPage.behavior.featureNames.soulmate'),
      birth_chart: t('reportsPage.behavior.featureNames.birthChart'),
      daily_fortune: t('reportsPage.behavior.featureNames.dailyFortune'),
      lucky_charm: t('reportsPage.behavior.featureNames.luckyCharm'),
      settings: t('reportsPage.behavior.featureNames.settings'),
      subscription: t('reportsPage.behavior.featureNames.subscription'),
    };
    return mapping[name] || name.replace(/_/g, ' ');
  };
}

function formatScreenName(name: string): string {
  return name.replace(/Screen$/, '').replace(/([A-Z])/g, ' $1').trim();
}

function getFunnelColor(index: number): string {
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  return colors[index % colors.length];
}
